import React, { forwardRef, useEffect, useState, useMemo } from 'react'
import { DataStore, API } from 'aws-amplify'
import {
  AppUser,
  UserGroup,
  DailyLog,
  DayDiagnoseComment,
  DayDiagnosis,
  DiagnoseType,
  DiagnosisSummary,
  Farm,
  Photo,
  Category,
  UserType,
} from '../../models'
import {
  StyleSheet,
  Text,
  ScrollView,
  TouchableOpacity,
  View,
  SafeAreaView,
} from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { getStorage, saveStorage } from '../components/SessionStorage'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import ja from 'date-fns/locale/ja'
import { Checkbox } from 'react-native-paper'
import { googleMapsApiKey } from '../components/AppConst'
import {
  formatToJapanDate,
  formatDate,
  getCurrentUser,
  S3Image,
  varietiesCnv,
  makeMultipleLines1Line,
} from '../components/CmnUtil'
import { CommentDialog } from '../components/CmnDiaLog'
import { useIsFocused } from '@react-navigation/native'
import IconImages from '../components/IconImages'
import { GraphQLResult } from '@aws-amplify/api-graphql'
import {
  getAppUser,
  getUserGroup,
  listDailyLogsByFarm,
  listDayDiagnoseCommentsByDayDiagnosis,
  listDayDianosesByFarm,
  listDiagnosisSummariesByDayDiagnosis,
  listFarmsByGroup,
  listPhotosByDayDiagnosis,
} from '../../graphql/queries'
import {
  GetAppUserQuery,
  GetUserGroupQuery,
  ListDailyLogsByFarmQuery,
  ListDayDiagnoseCommentsByDayDiagnosisQuery,
  ListDayDianosesByFarmQuery,
  ListDiagnosisSummariesByDayDiagnosisQuery,
  ListFarmsByGroupQuery,
  ListPhotosByDayDiagnosisQuery,
} from '../../API'

type DayDiagnosisType = {
  id: string
  photos: string[]
  date: string
  farmName: string
  avgVegetationCoverage: number | undefined
  avgBranchLength: number | undefined
  avgFloweringRate: number | undefined
  avgBearingNumber: number | undefined
  avgFruitSize: number | undefined
  fruitMaxSize: number | undefined
  fruitMinSize: number | undefined
  dailyLogsCategories: string[]
  comment: string
  commentUserIcon: string
  commentCount: number
}

/** ================================
 *  診断結果一覧画面
 *  ================================*/
const DiagnosisResultList = ({
  navigation,
  route,
}: {
  navigation: any
  route: any
}) => {
  // ユーザー取得
  async function queryAppUserById(id: string) {
    const res = (await API.graphql({
      query: getAppUser,
      variables: {
        id,
      },
    })) as GraphQLResult<GetAppUserQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.getAppUser) {
      const user = res.data.getAppUser
      return user as unknown as AppUser // 実質同じだが定義元が異なるので強制キャスト
    }
    return null
  }

  // 組織取得
  async function queryUserGroupById(id: string) {
    const res = (await API.graphql({
      query: getUserGroup,
      variables: {
        id,
      },
    })) as GraphQLResult<GetUserGroupQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.getUserGroup) {
      const user = res.data.getUserGroup
      return user as unknown as UserGroup // 実質同じだが定義元が異なるので強制キャスト
    }
    return null
  }

  // 圃場取得
  async function queryFarmsByGroup(groupID: string) {
    const res = (await API.graphql({
      query: listFarmsByGroup,
      variables: {
        groupID: groupID,
      },
    })) as GraphQLResult<ListFarmsByGroupQuery>
    if (res.errors) {
      throw res.errors
    }
    const userGroup = await queryUserGroupById(groupID)
    if (res.data && res.data.listFarmsByGroup) {
      const items = res.data.listFarmsByGroup.items.filter((c) => !c?._deleted)
      // DataStoreアクセスではuserオブジェクトも一緒に格納されており、UIはそれを参照する実装のため付加して返す
      // TODO: codegen時のdepthを変えればオブジェクトとして取得できるのか？
      const farms = items.map((item) =>
        Object.assign({ group: userGroup }, item)
      ) as Farm[]
      return farms
    }
    return []
  }

  // 日別AI診断結果取得
  async function queryDayDiagnosesByFarm(
    farmID: string,
    sortDirection: string
  ) {
    const res = (await API.graphql({
      query: listDayDianosesByFarm,
      variables: {
        farmID,
        sortDirection,
      },
    })) as GraphQLResult<ListDayDianosesByFarmQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.listDayDianosesByFarm) {
      const dayDiagnoses = res.data.listDayDianosesByFarm.items.filter(
        (c) => !c?._deleted
      )
      return dayDiagnoses as unknown as DayDiagnosis[] // 実質同じだが定義元が異なるので強制キャスト
    }
    return []
  }

  // 日別診断結果コメント取得
  async function queryDayDiagnoseCommentsByDayDiagnosis(
    dayDiagnosisID: string
  ) {
    const res = (await API.graphql({
      query: listDayDiagnoseCommentsByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        sortDirection: 'DESC',
      },
    })) as GraphQLResult<ListDayDiagnoseCommentsByDayDiagnosisQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.listDayDiagnoseCommentsByDayDiagnosis) {
      // TODO: _deletedの除外はこうするしかないのか？
      const items = res.data.listDayDiagnoseCommentsByDayDiagnosis.items.filter(
        (c) => !c?._deleted
      )
      return items as unknown as DayDiagnoseComment[] // 実質同じだが定義元が異なるので強制キャスト
    }
    return []
  }

  // 日次集計用AI診断結果取得
  async function queryDiagnosisSummariesByDayDiagnosis(dayDiagnosisID: string) {
    const res = (await API.graphql({
      query: listDiagnosisSummariesByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        limit: 10000
      },
    })) as GraphQLResult<ListDiagnosisSummariesByDayDiagnosisQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.listDiagnosisSummariesByDayDiagnosis) {
      const items = res.data.listDiagnosisSummariesByDayDiagnosis.items.filter(
        (c) => !c?._deleted
      )
      return items as unknown as DiagnosisSummary[] // 実質同じだが定義元が異なるので強制キャスト
    }
    return []
  }

  // 撮影画像取得
  async function queryPhotosByDayDiagnosis(dayDiagnosisID: string) {
    const res = (await API.graphql({
      query: listPhotosByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        sortDirection: 'DESC',
        limit: 1000,
      },
    })) as GraphQLResult<ListPhotosByDayDiagnosisQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.listPhotosByDayDiagnosis) {
      const items = res.data.listPhotosByDayDiagnosis.items.filter(
        (c) => !c?._deleted
      )
      return items as unknown as Photo[] // 実質同じだが定義元が異なるので強制キャスト
    }
    return []
  }

  // 作業日誌取得
  async function queryDailyLogsByFarmAndDate(farmID: string, date: string) {
    const res = (await API.graphql({
      query: listDailyLogsByFarm,
      variables: {
        farmID,
        // TODO: dateCategory: {eq: {date}} こうしたいがフォーマットエラーになる。未解決
        filter: { date: { eq: date } },
        limit: 2000,
      },
    })) as GraphQLResult<ListDailyLogsByFarmQuery>
    if (res.errors) {
      throw res.errors
    }
    if (res.data && res.data.listDailyLogsByFarm) {
      const items = res.data.listDailyLogsByFarm.items.filter(
        (c) => !c?._deleted
      )
      return items as unknown as DailyLog[] // 実質同じだが定義元が異なるので強制キャスト
    }
    return []
  }

  // ユーザーに紐づく圃場を格納するState
  const [farms, setFarms] = useState<Farm[]>([])

  // 圃場所有者のアイコン
  const [producerIcon, setProducerIcon] = useState<string>('')

  // farmsの圃場を指定するためのState→farms[farmNum]
  const [farmNum, setFarmNum] = useState<number>(0)

  // 圃場に紐づく診断結果の一覧を格納するState
  const [diagnosisData, setDiagnosisData] = useState<DayDiagnosisType[]>([])

  // コメント対象の診断結果のidを格納するState
  const [dayDiagnosisId, setDayDiagnosisId] = useState<string>('')

  // コメントダイアログフラグ格納State
  const [commentDialogFlag, setCommentDialogFlag] = useState<boolean>(false)

  // 並べ替え順格納State true→ascending
  const [sort, setSort] = useState<boolean>(false)

  // 絞り込みダイアログフラグ格納State
  const [narrowingFlag, setNarrowingFlag] = useState<boolean>(false)

  // 絞り込み内容反映用State
  const [reflection, setReflection] = useState({
    dateSpecification: false,
    fromDate: formatDate(new Date()),
    toDate: formatDate(new Date()),
    haveComment: false,
  })

  // googleMap
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey,
  })

  // 再レンダリング用
  const isFocused = useIsFocused()

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓クリック処理↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  // 「他の圃場」クリック
  const farmChange = async () => {
    const nextFarmNum = (farmNum + 1) % farms.length
    setFarmNum(nextFarmNum)
    // セッションストレージに保存されているfarmをupdate
    await saveStorage('farm', farms[nextFarmNum])
  }

  // 並べ替えボタンクリック
  const switchSort = () => {
    setSort(!sort)
    setDiagnosisData(diagnosisData.reverse())
  }

  /**
   * 診断結果の吹き出しをクリック
   * @param id
   */
  const onClickDayDiagnosisComment = (id: string) => {
    setDayDiagnosisId(id)
    setCommentDialogFlag(true)
  }

  /**
   * コメントダイアログの「☓」ボタンをクリック
   */
  const onClickClose = () => {
    setCommentDialogFlag(false)
  }

  async function specifyProducerToShow() {
    const appUser = await getCurrentUser()
    if (!appUser) {
      throw 'getCurrentUser() returns undefined.'
    }
    if (appUser.group.groupType === UserType.PRODUCER) {
      return appUser.group.id
    }
    // 指導員の場合、現在選択中の生産者を表示
    const tmpProducer = await getStorage('friendProducer')
    if (!tmpProducer) {
      throw "getStorage('friendProducer') returns undefined."
    }
    return tmpProducer
  }

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓useEffect↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  // farmsに圃場を格納
  async function init() {
    const groupID = await specifyProducerToShow()
    let farmData = await queryFarmsByGroup(groupID)

    // セッションストレージからfarmIdを取得して、その圃場を選択
    const res = await getStorage('farm')
    let farmId = !!res ? res.id : ''
    if (farmData.length > 0 && !farmData.find((f) => f.id === farmId)) {
      // 友達が切り替わった場合
      farmId = farmData[0].id
      await saveStorage('farm', farmData[0])
    }

    farmData.forEach((farm, index) => {
      if (farm.id == farmId) {
        setFarmNum(index)
      }
    })

    setFarms(farmData)
  }

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    // 画面フォーカス時に再描画
    if (isFocused) {
      init()
    }
  }, [isFocused])

  // farms[farmNum]のIDをもとに診断結果を格納
  useEffect(() => {
    fetchFarmData()
  }, [farmNum, farms])

  // 購読
  useEffect(() => {
    const dayDiagnosisSubscribe = DataStore.observe(
      DayDiagnoseComment
    ).subscribe((value) => {
      const tmpValue = value
      if (tmpValue.opType != 'INSERT' && tmpValue.opType != 'DELETE') {
        return
      }
      let changeData = diagnosisData
      changeData.forEach(async (diagnosis, key) => {
        if (diagnosis.id === dayDiagnosisId) {
          const currentComments = await queryDayDiagnoseCommentsByDayDiagnosis(
            dayDiagnosisId
          )
          // コメント件数を更新
          changeData[key].commentCount = currentComments.length
          // コメントを更新
          changeData[key].comment = tmpValue.element.text
          // アイコンがあれば表示する
          const appUser = await queryAppUserById(tmpValue.element.authorID)
          changeData[key].commentUserIcon =
            appUser && appUser.icon ? appUser.icon : ''
        }
      })
      setDiagnosisData(changeData)
    })

    return () => {
      dayDiagnosisSubscribe.unsubscribe()
    }
  }, [commentDialogFlag, dayDiagnosisId])

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓補助関数↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  /**
   * 圃場一覧データ格納
   */
  const fetchFarmData = async () => {
    if (farms.length == 0) {
      return
    }
    const farmID: string = farms[farmNum].id

    ;(async () => {
      // 診断結果を取得
      let dayDiagnosisData: DayDiagnosis[] = await queryDayDiagnosesByFarm(
        farmID,
        sort ? 'ASC' : 'DESC'
      )
      dayDiagnosisData = dayDiagnosisData.filter((d) => d.isAggregated)

      // diagnosisDataに入れるデータを作成
      const addData: DayDiagnosisType[] = await Promise.all(
        dayDiagnosisData.map(async (dayDiagnosis) => {
          // 診断結果に紐づいた写真をすべて取得
          let photos: Photo[] = await queryPhotosByDayDiagnosis(dayDiagnosis.id)

          // 写真のキーを取得
          const photoKeys: string[] = photos.map((photo) => photo.photoKey)

          // 日付を取得
          const date = formatDate(new Date(dayDiagnosis.date))

          // 圃場名を取得
          const farmName = farms[farmNum].name

          // 診断結果平均を取得
          const diagnosisSummary: DiagnosisSummary[] =
            await await queryDiagnosisSummariesByDayDiagnosis(dayDiagnosis.id)

          // 植被率平均を取得
          const dayDiagnosisAvgVegetationCoverage: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.VEGETATION_COVERAGE
            )
          let avgVegetationCoverage: number | undefined = 0
          if (dayDiagnosisAvgVegetationCoverage.length != 0) {
            avgVegetationCoverage =
              dayDiagnosisAvgVegetationCoverage[0].diagnoseResult
          } else {
            avgVegetationCoverage = undefined
          }

          // 枝の長さ平均を取得
          const dayDiagnosisAvgBranchLength: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.BRANCH_LENGTH
            )
          let avgBranchLength: number | undefined = 0
          if (dayDiagnosisAvgBranchLength.length != 0) {
            avgBranchLength = dayDiagnosisAvgBranchLength[0].diagnoseResult
          } else {
            avgBranchLength = undefined
          }

          // 開花率平均を取得
          const dayDiagnosisAvgFloweringRate: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.FLOWERING_RATE
            )
          let avgFloweringRate: number | undefined = 0
          if (dayDiagnosisAvgFloweringRate.length != 0) {
            avgFloweringRate = dayDiagnosisAvgFloweringRate[0].diagnoseResult
          } else {
            avgFloweringRate = undefined
          }

          // 着果数平均を取得
          const dayDiagnosisAvgBearingNumber: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.BEARING_NUMBER
            )
          let avgBearingNumber: number | undefined = 0
          if (dayDiagnosisAvgBearingNumber.length != 0) {
            avgBearingNumber = dayDiagnosisAvgBearingNumber[0].diagnoseResult
          } else {
            avgBearingNumber = undefined
          }

          // 果実サイズ平均を取得
          const dayDiagnosisAvgFruitSize: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.FRUIT_SIZE
            )
          let avgFruitSize: number | undefined = 0
          if (dayDiagnosisAvgFruitSize.length != 0) {
            avgFruitSize = dayDiagnosisAvgFruitSize[0].diagnoseResult
          } else {
            avgFruitSize = undefined
          }

          // 最大果実サイズを取得
          const dayDiagnosisFruitMaxSize: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.FRUIT_MAX_SIZE
            )
          let fruitMaxSize: number | undefined = 0
          if (dayDiagnosisFruitMaxSize.length != 0) {
            fruitMaxSize = dayDiagnosisFruitMaxSize[0].diagnoseResult
          } else {
            fruitMaxSize = undefined
          }

          // 最小果実サイズを取得
          const dayDiagnosisFruitMinSize: DiagnosisSummary[] =
            diagnosisSummary.filter(
              (c) => c.diagnoseType == DiagnoseType.FRUIT_MIN_SIZE
            )
          let fruitMinSize: number | undefined = 0
          if (dayDiagnosisFruitMinSize.length != 0) {
            fruitMinSize = dayDiagnosisFruitMinSize[0].diagnoseResult
          } else {
            fruitMinSize = undefined
          }

          // その日の作業日誌を取得
          let dailyLogs: DailyLog[] = await queryDailyLogsByFarmAndDate(
            farmID,
            date
          )

          // 作業日誌のカテゴリーを変換して取得
          const dailyLogsCategories: string[] = dailyLogs.map(
            (dailyLog): string => {
              switch (dailyLog.category) {
                case Category.FRUITS_THINNING:
                  return '摘果'
                case Category.FLOWERING:
                  return '開花'
                case Category.HARVEST:
                  return '収穫'
                case Category.MATING:
                  return '交配'
                case Category.PESTICIDE_SPRAYNING:
                  return '農薬散布'
                case Category.OTHERS:
                  return dailyLog.categoryOptional || 'その他'
                default:
                  return 'その他'
              }
            }
          )

          // 診断結果に紐づくコメントの最新を取得
          let dayDiagnosisComment: DayDiagnoseComment[] = []

          dayDiagnosisComment = await queryDayDiagnoseCommentsByDayDiagnosis(
            dayDiagnosis.id
          )

          // コメントしたユーザーを取得
          let commentUser: AppUser | null = null
          if (dayDiagnosisComment.length != 0) {
            commentUser = await queryAppUserById(
              dayDiagnosisComment[0].authorID
            )
          }

          // コメントしたユーザーのアイコンを取得
          const commentUserIcon: string =
            commentUser && commentUser.icon ? commentUser.icon : ''

          const add: DayDiagnosisType = {
            id: dayDiagnosis.id,
            photos: photoKeys,
            date: date,
            farmName: farmName,
            avgVegetationCoverage: avgVegetationCoverage,
            avgBranchLength: avgBranchLength,
            avgBearingNumber: avgBearingNumber,
            avgFloweringRate: avgFloweringRate,
            avgFruitSize: avgFruitSize,
            fruitMaxSize: fruitMaxSize,
            fruitMinSize: fruitMinSize,
            dailyLogsCategories: dailyLogsCategories,
            comment:
              dayDiagnosisComment.length == 0
                ? ''
                : dayDiagnosisComment[0].text,
            commentUserIcon: commentUserIcon,
            commentCount: dayDiagnosisComment.length,
          }

          return add
        })
      )

      // 圃場所有者のアイコン格納
      const icon = farms[farmNum].group.icon
      icon && setProducerIcon(icon)

      setDiagnosisData(addData)
    })()
  }

  /**
   * 絞り込み結果を反映した診断結果一覧を返す
   * @returns DayDiagnosisType[]
   */
  const getFilterData = (): DayDiagnosisType[] => {
    let filterData = diagnosisData

    // 絞り込み項目で絞り込む
    if (reflection.dateSpecification) {
      filterData = filterData.filter((data) => {
        return (
          data.date <= reflection.toDate && data.date >= reflection.fromDate
        )
      })
    }
    if (reflection.haveComment) {
      filterData = filterData.filter((data) => {
        return data.comment != ''
      })
    }

    return filterData
  }

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓補助コンポーネント↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  // 診断結果一覧
  const Diagnosises = () => {
    const dayDiagnosises = getFilterData().map((data, key) => {
      return (
        <Diagnosis
          id={data.id}
          photos={data.photos}
          date={data.date}
          farmName={data.farmName}
          avgVegetationCoverage={data.avgVegetationCoverage}
          avgBranchLength={data.avgBranchLength}
          avgBearingNumber={data.avgBearingNumber}
          avgFloweringRate={data.avgFloweringRate}
          avgFruitSize={data.avgFruitSize}
          fruitMaxSize={data.fruitMaxSize}
          fruitMinSize={data.fruitMinSize}
          dailyLogsCategories={data.dailyLogsCategories}
          comment={data.comment}
          commentUserIcon={data.commentUserIcon}
          commentCount={data.commentCount}
          key={key}
        />
      )
    })

    return (
      <View>
        {/* 診断結果ラッパー */}
        {dayDiagnosises}
      </View>
    )
  }

  function getThumbKey(srcKey: string) {
    if (!srcKey) {
      return ''
    }
    const lastDot = srcKey.lastIndexOf('.')
    const namePart = srcKey.slice(0, lastDot)
    const ext = srcKey.slice(lastDot + 1)
    return `${namePart}_thumb.${ext}`
  }

  // 診断結果一枚分
  const Diagnosis = ({
    id,
    photos,
    date,
    farmName,
    avgVegetationCoverage,
    avgBranchLength,
    avgFloweringRate,
    avgBearingNumber,
    avgFruitSize,
    fruitMaxSize,
    fruitMinSize,
    dailyLogsCategories,
    comment,
    commentUserIcon,
    commentCount,
  }: DayDiagnosisType) => {
    const day = ['日', '月', '火', '水', '木', '金', '土']
    return (
      <TouchableOpacity
        style={styles.diagnosisReasultItem}
        onPress={() => navigation.navigate('DiagnosisPhotoList', { id: id })}
      >
        {/* 診断結果 */}
        <View style={styles.diagnosisphotoContainer}>
          {/* 診断写真コンテナー */}
          <S3Image
            resizeMode="cover"
            imageKey={getThumbKey(photos[0])}
            style={styles.diagnosisphoto}
          />
          {/* 診断写真 */}
          <S3Image
            resizeMode="cover"
            imageKey={getThumbKey(photos[1])}
            style={styles.diagnosisphoto}
          />
        </View>
        <View style={styles.diagnosisReasultDetailBox}>
          <View>
            {/* 診断詳細 */}
            {/* 日付 */}
            <Text style={styles.diagnosisDate}>
              {formatToJapanDate(new Date(date))}
            </Text>
            {/* 圃場名 */}
            <Text style={[styles.farmName, { marginBottom: 5 }]}>
              {farmName}
            </Text>
            {/* 撮影枚数 */}
            <Text style={styles.photoCount}>
              撮影枚数&#65306;{photos.length}枚
            </Text>
            {/* 植被率平均 */}
            {avgVegetationCoverage != undefined && (
              <Text style={styles.vegetationRateAvg}>
                植被率平均&#65306;
                {Number((avgVegetationCoverage * 100).toFixed(2))}&#037;
              </Text>
            )}
            {/* 枝の長さ平均 */}
            {avgBranchLength != undefined && (
              <Text style={styles.vegetationRateAvg}>
                枝の長さ平均&#65306;
                {Number(avgBranchLength.toFixed(2))}m
              </Text>
            )}
            {/* 開花率平均 */}
            {avgFloweringRate != undefined && (
              <Text style={styles.vegetationRateAvg}>
                開花率平均&#65306;
                {Number((avgFloweringRate * 100).toFixed(2))}&#037;
              </Text>
            )}
            {/* 着果数平均 */}
            {avgBearingNumber != undefined && (
              <Text style={styles.vegetationRateAvg}>
                着果数平均&#65306;
                {Number(avgBearingNumber.toFixed(2))}個
              </Text>
            )}
            {/* 果実サイズ平均 */}
            {avgFruitSize != undefined && (
              <Text style={styles.vegetationRateAvg}>
                果実サイズ平均&#65306;
                {Number(avgFruitSize.toFixed(2))}cm
              </Text>
            )}
            {/* 最大果実サイズ */}
            {fruitMaxSize != undefined && (
              <Text style={styles.vegetationRateAvg}>
                最大果実サイズ&#65306;
                {Number(fruitMaxSize.toFixed(2))}cm
              </Text>
            )}
            {/* 最小果実サイズ */}
            {fruitMinSize != undefined && (
              <Text style={styles.vegetationRateAvg}>
                最小果実サイズ&#65306;
                {Number(fruitMinSize.toFixed(2))}cm
              </Text>
            )}
            {/* 作業日誌 */}
            <View style={styles.workLogContainer}>
              <Text style={{ fontSize: 15 }}>作業日誌&#65306;</Text>
              <View style={styles.workLogs}>
                <Text style={{ fontSize: 15 }}>
                  {dailyLogsCategories.map((item) => item + '\n')}
                </Text>
              </View>
            </View>
          </View>
          <TouchableOpacity
            style={styles.commentContainer}
            onPress={() => onClickDayDiagnosisComment(id)}
          >
            <View style={styles.commentBox}>
              {/* 診断コメント */}
              {
                commentUserIcon == '' ? (
                  <View></View>
                ) : (
                  <S3Image
                    resizeMode="contain"
                    imageKey={commentUserIcon}
                    style={styles.userIcon}
                    defaultSource={IconImages.icUser}
                  />
                ) // ユーザーアイコン
              }
              <Text
                style={styles.comment}
                numberOfLines={2}
                ellipsizeMode={'tail'}
              >
                {makeMultipleLines1Line(comment)}
              </Text>
              {/* コメント */}
            </View>
            <View style={styles.createComment}>
              <MaterialCommunityIcons
                name={'chat-processing-outline'}
                size={28}
              />
              <Text>{commentCount}件</Text>
            </View>
            {/* コメント作成？ */}
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    )
  }

  // 栽培品種
  const FarmVarieties = ({ varieties }: { varieties: any }) => {
    const tmpVarieties = []
    if (varieties) {
      for (const variety of varieties) {
        if (variety) {
          tmpVarieties.push(varietiesCnv(variety))
        }
      }
    }
    return (
      <Text
        style={styles.farmVarieties}
        numberOfLines={1}
        ellipsizeMode={'tail'}
      >
        {tmpVarieties.join('・')}
      </Text>
    )
  }

  const ResultList = useMemo(() => {
    return (
      <ScrollView style={styles.container}>
        {farms.length == 0 ? (
          <View style={styles.wrapper}>
            <Text
              style={{
                fontSize: 25,
                fontWeight: 'bold',
                textAlign: 'center',
                marginTop: 20,
              }}
            >
              圃場が登録されていません。{'\n'}
              「設定」→「圃場管理」から圃場の登録を行ってください。
            </Text>
          </View>
        ) : (
          <View style={styles.wrapper}>
            <View style={styles.farmInfo}>
              {/* 圃場情報 */}

              {loadError ? (
                <Text>現在、地図を読み込むことができません。</Text>
              ) : isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '150px' }}
                  center={{ lat: farms[farmNum].lat, lng: farms[farmNum].lng }}
                  zoom={18}
                  options={{
                    zoomControl: false,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    mapTypeId: 'hybrid',
                    gestureHandling: 'none',
                    keyboardShortcuts: false,
                  }}
                >
                  <Marker
                    position={{
                      lat: farms[farmNum].lat,
                      lng: farms[farmNum].lng,
                    }}
                  />
                </GoogleMap>
              ) : (
                <Text>loading...</Text>
              )}

              <View style={styles.userInfo}>
                <S3Image
                  resizeMode="contain"
                  imageKey={producerIcon}
                  style={styles.userIcon}
                  defaultSource={IconImages.icGroup}
                />
                <Text style={styles.groupName}>
                  {farms[farmNum].group.groupName}
                </Text>
                {/* ユーザー名 */}
              </View>
              <Text style={[styles.farmName, { marginBottom: 10 }]}>
                {farms[farmNum].name}
              </Text>
              {/* 圃場名 */}
              <FarmVarieties varieties={farms[farmNum].varieties} />
              {/* 栽培品種 */}
              <Text
                style={styles.farmMemo}
                numberOfLines={1}
                ellipsizeMode={'tail'}
              >
                {farms[farmNum].memo}
              </Text>
              {/* 圃場メモ */}
              <TouchableOpacity
                style={styles.farmChangeButton}
                onPress={farmChange}
              >
                {/* 圃場切り替えボタン */}
                <Text style={{ textDecorationLine: 'underline' }}>
                  ⇒他の圃場
                </Text>
              </TouchableOpacity>
            </View>
            {diagnosisData.length > 0 && (
              <TouchableOpacity
                style={styles.dataComparisonButton}
                onPress={() => navigation.navigate('DiagnosisCompare')}
              >
                <Text style={{ color: '#ffffff' }}>過去データと比較</Text>
              </TouchableOpacity>
            )}
            {/* 過去情報比較ボタン */}
            <View>
              {/* 診断結果コンテナ― */}
              <View style={styles.diagnosisReasultHeader}>
                <Text style={styles.resultTitle}>診断結果一覧</Text>
                {/* 診断結果一覧テキスト */}
                <View style={styles.buttonContainer}>
                  <TouchableOpacity onPress={switchSort}>
                    {/* 並べ替えボタン */}
                    <Text style={styles.sortingText}>↑↓</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.refinementButton}
                    onPress={() => setNarrowingFlag(true)}
                  >
                    {/* 絞り込みボタン */}
                    <MaterialCommunityIcons
                      style={styles.refinementIcon}
                      name={'filter-outline'}
                      size={28}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {diagnosisData.length > 0 ? (
                <Diagnosises />
              ) : (
                <Text
                  style={{
                    fontSize: 25,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginTop: 20,
                  }}
                >
                  この圃場ではまだ撮影・AI診断が行われていません。
                </Text>
              )}
            </View>
          </View>
        )}
      </ScrollView>
    )
  }, [farms, producerIcon, farmNum, diagnosisData, sort, reflection])

  return (
    <SafeAreaView style={styles.container}>
      {/* 診断結果一覧 */}
      {ResultList}

      {/* 絞り込みダイアログ */}
      {useMemo(() => {
        return (
          <NarrowingDialog
            narrowingFlag={narrowingFlag}
            reflection={reflection}
            setReflection={setReflection}
            setNarrowingFlag={setNarrowingFlag}
          />
        )
      }, [narrowingFlag, reflection])}

      {/* コメントダイアログ */}
      {useMemo(() => {
        return (
          <CommentDialog
            onClickClose={() => onClickClose()}
            id={dayDiagnosisId}
            type={'dayDiagnosis'}
            visible={commentDialogFlag}
          />
        )
      }, [dayDiagnosisId, commentDialogFlag])}
    </SafeAreaView>
  )
}

// 絞り込みダイアログ
const NarrowingDialog = ({
  narrowingFlag,
  reflection,
  setReflection,
  setNarrowingFlag,
}: {
  narrowingFlag: boolean
  reflection: any
  setReflection: Function
  setNarrowingFlag: Function
}) => {
  // 絞り込み日付フラグ格納State
  const [dateSpecification, setdateSpecification] = useState<boolean>(false)

  // 絞り込み開始日格納State
  const [fromDate, setFromDate] = useState<string>(formatDate(new Date()))

  // 絞り込み終了日格納State
  const [toDate, setToDate] = useState<string>(formatDate(new Date()))

  // 絞り込みコメントフラグ格納State
  const [haveComment, setHaveComment] = useState<boolean>(false)

  useEffect(() => {
    setdateSpecification(reflection.dateSpecification)
    setFromDate(reflection.fromDate)
    setToDate(reflection.toDate)
    setHaveComment(reflection.haveComment)
  }, [reflection])

  // 日付選択Picker
  const CustomDatePicker = ({
    onChange,
    selected,
  }: {
    onChange: Function
    selected: Date
  }) => {
    // @ts-ignore
    const CustomInput = forwardRef(({ onClick }, ref) => (
      <TouchableOpacity onPress={onClick} style={styles.dateInput}>
        <Text style={styles.dateText}>
          {formatToJapanDate(selected).slice(0, -3)}
        </Text>
      </TouchableOpacity>
    ))

    return (
      <DatePicker
        locale={ja}
        selected={selected}
        onChange={(date) => onChange(date!.toISOString().slice(0, 10))}
        customInput={<CustomInput />}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <View
            style={{
              padding: 10,
              flexDirection: 'row',
              width: 'fitContent',
            }}
          >
            <TouchableOpacity
              onPress={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <Text style={{ fontSize: 22, fontWeight: 'bold' }}>&lt;</Text>
            </TouchableOpacity>
            <View style={{ flexDirection: 'row', marginHorizontal: 'auto' }}>
              <Text style={{ fontSize: 22, fontWeight: 'bold' }}>
                {date.getFullYear()}年
              </Text>
              <Text style={{ fontSize: 22, fontWeight: 'bold' }}>
                {date.getMonth() + 1}月
              </Text>
            </View>
            <TouchableOpacity
              onPress={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <Text style={{ fontSize: 22, fontWeight: 'bold' }}>&gt;</Text>
            </TouchableOpacity>
          </View>
        )}
        dateFormat="yyyy年MM月dd日"
      />
    )
  }

  // 「絞り込む」ボタンクリック
  const onNarrowing = () => {
    setReflection({
      dateSpecification: dateSpecification,
      fromDate: fromDate,
      toDate: toDate,
      haveComment: haveComment,
    })
    setNarrowingFlag(false)
  }

  return narrowingFlag ? (
    <View
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: 'rgba(200,200,200, 0.5)',
        position: 'absolute',
        justifyContent: 'center',
      }}
    >
      <View style={styles.narrowingDialog}>
        <View style={styles.itemWrapper}>
          <View style={[styles.dialogBox, { zIndex: 3 }]}>
            <Checkbox
              status={dateSpecification ? 'checked' : 'unchecked'}
              onPress={() => {
                setdateSpecification(!dateSpecification)
              }}
              color="#507E32"
            />
            {dateSpecification ? (
              <View>
                <View style={[styles.dateItem, { zIndex: 3 }]}>
                  <Text style={[styles.dialogText, { width: 120 }]}>
                    開始日&#65306;
                  </Text>
                  <CustomDatePicker
                    onChange={setFromDate}
                    selected={new Date(fromDate)}
                  />
                </View>
                <View style={styles.dateItem}>
                  <Text style={[styles.dialogText, { width: 120 }]}>
                    終了日&#65306;
                  </Text>
                  <CustomDatePicker
                    onChange={setToDate}
                    selected={new Date(toDate)}
                  />
                </View>
              </View>
            ) : (
              <TouchableOpacity
                onPress={() => setdateSpecification(!dateSpecification)}
              >
                <Text style={{ fontSize: 20 }}>日付を指定する</Text>
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.dialogBox}>
            <Checkbox
              status={haveComment ? 'checked' : 'unchecked'}
              onPress={() => {
                setHaveComment(!haveComment)
              }}
              color="#507E32"
            />
            <TouchableOpacity onPress={() => setHaveComment(!haveComment)}>
              <Text style={styles.dialogText}>コメント有のみ</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity style={styles.dialogButton} onPress={onNarrowing}>
            <Text style={styles.dialogButtonText}>絞り込む</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  ) : (
    <></>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E2F0D9',
  },
  wrapper: {
    width: '90%',
    justifyContent: 'center',
    marginHorizontal: 'auto',
  },
  farmInfo: {
    width: '100%',
    backgroundColor: '#ffffff',
    padding: 5,
    alignItems: 'center',
    shadowColor: '#808080',
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
    marginBottom: 10,
  },
  userInfo: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    marginVertical: 10,
  },
  userIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 10,
    backgroundColor: '#808080',
  },
  groupName: {
    fontWeight: 'bold',
  },
  farmName: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  farmVarieties: {
    fontWeight: 'bold',
  },
  farmMemo: {
    marginBottom: 15,
  },
  farmChangeButton: {
    position: 'absolute',
    right: 5,
    bottom: 5,
  },
  dataComparisonButton: {
    backgroundColor: '#70AD47',
    width: 140,
    borderColor: '#507E32',
    borderStyle: 'solid',
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  diagnosisReasultHeader: {
    marginBottom: 10,
    position: 'relative',
  },
  resultTitle: {
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 'bold',
  },
  buttonContainer: {
    position: 'absolute',
    right: 0,
    flexDirection: 'row',
  },
  sortingText: {
    fontSize: 25,
    fontWeight: 'bold',
    lineHeight: 25,
  },
  refinementButton: {
    justifyContent: 'center',
    marginLeft: 10,
  },
  refinementIcon: {
    width: 28,
    height: 28,
  },
  diagnosisReasultItem: {
    shadowColor: '#808080',
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
    flexDirection: 'row',
    padding: 5,
    backgroundColor: '#ffffff',
    marginBottom: 20,
  },
  diagnosisphotoContainer: {
    flex: 0.4,
    justifyContent: 'space-between',
    minHeight: 150,
    marginRight: 10,
  },
  diagnosisphoto: {
    width: '100%',
    flex: 0.475,
  },
  diagnosisReasultDetailBox: {
    flex: 0.6,
    justifyContent: 'space-between',
  },
  diagnosisDate: {
    fontWeight: 'bold',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 5,
    marginTop: 20,
  },
  photoCount: {
    fontSize: 15,
    marginBottom: 5,
  },
  vegetationRateAvg: {
    fontSize: 15,
    marginBottom: 5,
  },
  workLogContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginBottom: 20,
  },
  workLogs: {
    width: 'fitContent',
  },
  commentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopColor: '#70ad47',
    borderTopWidth: 2,
    borderStyle: 'solid',
    paddingVertical: 10,
    justifyContent: 'space-between',
  },
  commentBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
  },
  comment: {
    marginRight: 10,
  },
  createComment: {
    width: 29,
    alignItems: 'center',
  },
  // 絞り込みダイアログのスタイル
  narrowingDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E2F0D9',
    paddingVertical: 15,
    borderColor: '#507E32',
    borderWidth: 2,
    borderStartColor: 'solid',
    position: 'absolute',
    top: '30%',
    width: '100%',
  },
  itemWrapper: {
    width: 'fitContent',
  },
  dateItem: {
    flexDirection: 'row',
    width: '100%',
    paddingHorizontal: 10,
    marginBottom: 5,
    alignItems: 'center',
    alignContent: 'center',
    zIndex: -1,
  },
  dateInput: {
    width: 'fitContent',
    height: 35,
    borderColor: '#008000',
    borderWidth: 2,
    borderStyle: 'solid',
    backgroundColor: '#ffffff',
  },
  dateText: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    justifyContent: 'center',
  },
  dialogBox: {
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  dialogText: {
    fontSize: 20,
    width: 'fitContent',
  },
  dialogDateText: {
    fontWeight: 'bold',
    borderBottomColor: '#008000',
    borderBottomWidth: 2,
    borderStartColor: 'solid',
    fontSize: 22,
  },
  dialogButton: {
    backgroundColor: '#70AD47',
    width: 150,
    height: 30,
    shadowColor: '#808080',
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
    borderColor: '#507E32',
    borderStyle: 'solid',
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 'auto',
  },
  dialogButtonText: {
    color: '#ffffff',
    fontSize: 18,
    textAlign: 'center',
    justifyContent: 'center',
  },
})

export default DiagnosisResultList
